import { Suspense, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/cn';
import { initials } from '@/lib/format';
import { Icon, type IconName } from '@/components/ui/Icon';
import { Logo, LogoMark } from './Logo';
import { Spinner } from '@/components/ui/Spinner';
import { ErrorBlock, LoadingBlock } from '@/components/ui/Misc';
import { errorMessage } from '@/api/errors';
import { useEmisor, useEmisorMetadata } from '@/api/hooks/emisor';
import { onboardingSkipped, onboardingStep } from '@/features/onboarding/status';
import { useAuth } from '@/auth/AuthProvider';
import { useTheme } from '@/lib/theme';
import { usePushRuntime } from '@/push/usePush';

const NAV: { to: string; key: 'inicio' | 'comprobantes' | 'clientes' | 'productos'; icon: IconName }[] = [
  { to: '/', key: 'inicio', icon: 'home' },
  { to: '/comprobantes', key: 'comprobantes', icon: 'file' },
  { to: '/clientes', key: 'clientes', icon: 'users' },
  { to: '/productos', key: 'productos', icon: 'box' },
];

/**
 * Pinned to the bottom with Cuenta rather than listed with the day-to-day sections: it is set
 * up once and then left alone, like the account settings under it.
 *
 * Always listed, never hidden by plan — whether the API is included is the server's answer
 * (403 API_KEYS_NOT_INCLUDED), and the section itself shows what the plan would unlock.
 */
const API_NAV = { to: '/integraciones', key: 'api', icon: 'key' } as const;

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useTranslation();
  const { data: meta } = useEmisorMetadata();
  const { session } = useAuth();
  const name = meta?.razonSocial || meta?.nombreComercial || session?.email || 'Facturita';
  const sub = meta?.ruc || session?.email || '';

  return (
    <aside className="w-[230px] shrink-0 bg-surface border-r border-line flex flex-col p-3.5 pt-5 h-full">
      <div className="px-2.5 pb-[22px] pt-1.5">
        <Logo />
      </div>
      <nav className="flex flex-col gap-1" aria-label={t('common.a11y.mainNav')}>
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === '/'}
            onClick={onNavigate}
            className={({ isActive }) => cn('nav-item', isActive && 'active')}
          >
            <Icon name={n.icon} size={19} />
            {t(`nav.${n.key}`)}
          </NavLink>
        ))}
      </nav>
      <div className="flex-1" />
      <NavLink to={API_NAV.to} onClick={onNavigate} className={({ isActive }) => cn('nav-item', isActive && 'active')}>
        <Icon name={API_NAV.icon} size={19} />
        {t(`nav.${API_NAV.key}`)}
      </NavLink>
      <NavLink to="/cuenta" onClick={onNavigate} className={({ isActive }) => cn('nav-item', isActive && 'active')}>
        <Icon name="settings" size={19} />
        {t('nav.cuenta')}
      </NavLink>
      <NavLink
        to="/cuenta"
        onClick={onNavigate}
        className="border-t border-line mt-2.5 pt-3.5 flex items-center gap-3 px-1 hover:opacity-80"
        aria-label={t('nav.cuenta')}
      >
        {/* The business identity informs, it is not a control: second accent. */}
        <div className="w-9 h-9 rounded-full bg-alt text-on-alt flex items-center justify-center font-display font-bold text-[13px] shrink-0">
          {initials(name)}
        </div>
        <div className="leading-tight min-w-0">
          <div className="text-[13px] font-bold truncate">{name}</div>
          <div className="text-[11.5px] text-muted-2 truncate">{sub}</div>
        </div>
      </NavLink>
    </aside>
  );
}

export function AppShell() {
  useTheme();
  usePushRuntime();
  const { t } = useTranslation();
  const [drawer, setDrawer] = useState(false);
  const loc = useLocation();
  const emisorQ = useEmisor();

  // Hold the shell on a splash until the first emisor check resolves so the dashboard never
  // flashes before the redirect.
  if (emisorQ.isPending && !emisorQ.data) {
    return (
      <div className="h-dvh bg-app flex flex-col items-center justify-center gap-4">
        <LogoMark size={56} />
        <Spinner className="text-primary" />
      </div>
    );
  }

  // A read that failed with nothing cached tells us nothing about whether setup is done. Offer a
  // retry rather than guessing, because guessing here means sending a configured account back
  // through onboarding.
  if (emisorQ.isError && !emisorQ.data) {
    return (
      <div className="h-dvh bg-app flex flex-col items-center justify-center gap-4 px-6">
        <LogoMark size={56} />
        <div className="w-full max-w-[420px]">
          <ErrorBlock message={errorMessage(emisorQ.error)} onRetry={() => void emisorQ.refetch()} retryLabel={t('common.retry')} />
        </div>
      </div>
    );
  }

  // New account (no emisor / no establecimiento+punto): go through the guided setup first.
  // Only a *successful* read may redirect. `isSuccess` goes false the moment a refetch fails, so a
  // partial or cancelled load can no longer masquerade as "this account has no establecimientos".
  if (emisorQ.isSuccess && onboardingStep(emisorQ.data) < 3 && !onboardingSkipped()) {
    // Say why. Being sent back through setup is drastic and the cause is always in this data, so
    // leave a trace rather than making the next report start from scratch.
    const e = emisorQ.data;
    console.warn('[onboarding] redirecting to setup — step %d', onboardingStep(e), {
      ruc: e?.ruc || '(missing)',
      razonSocial: e?.razonSocial || '(missing)',
      establecimientos: e?.establecimientos.length ?? 0,
      puntosPerEstablecimiento: e?.establecimientos.map((x) => x.puntos.length) ?? [],
    });
    return <Navigate to="/onboarding" replace />;
  }
  return (
    <div className="flex h-dvh bg-app overflow-hidden">
      <div className="hidden lg:block h-full">
        <Sidebar />
      </div>
      {drawer && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button type="button" className="absolute inset-0 bg-[rgba(20,22,28,0.5)]" aria-label={t('common.a11y.closeMenu')} onClick={() => setDrawer(false)} />
          <div className="absolute inset-y-0 left-0 shadow-modal">
            <Sidebar onNavigate={() => setDrawer(false)} />
          </div>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col h-full">
        <header className="lg:hidden flex items-center gap-3 px-4 py-3 bg-surface border-b border-line">
          <button type="button" onClick={() => setDrawer(true)} aria-label={t('common.a11y.openMenu')} className="p-1.5 -ml-1.5 rounded-lg hover:bg-app">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
          <LogoMark size={24} />
          <span className="font-display font-bold text-lg">Facturita</span>
        </header>
        <main key={loc.pathname} className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
          <div className="px-4 py-5 sm:px-6 lg:px-[30px] lg:py-6 max-w-[1440px] w-full min-w-0 min-h-full mx-auto flex flex-col gap-[18px]">
            {/* Every page is a lazy chunk. Without a boundary here the only one is the router's,
                above this shell, so the first visit to a route tears down the sidebar and the
                header too and the whole window blinks. Suspending inside the content area keeps
                the chrome mounted and replaces only what is actually loading. */}
            <Suspense fallback={<LoadingBlock />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
